/**
 * The chat pane: header, transcript, composer, and the queue.
 *
 * The transcript is the only scrolling region — the header and composer are
 * outside it — so scrolling never moves the controls out from under a thumb.
 */

import { useEffect, useRef, useState } from "react";
import { useAppState, useStore } from "./store-context";
import { Composer } from "./composer";
import { Transcript } from "./transcript";
import { IconBack, IconBranch, IconDevice } from "./icons";
import { ChangeRequestButton, ChangeRequestPanel } from "./change-requests";
import { chatTitle, spaceLabel } from "../lib/models";

export function ChatPane({ onBack }: { onBack: () => void }) {
  const state = useAppState();
  const store = useStore();
  const chat = state.chats.find((candidate) => candidate.id === state.openChatId);
  const scroller = useRef<HTMLDivElement>(null);
  /** The change-request panel, closed by default: it is a side trip, not the
   * daily loop. */
  const [panelOpen, setPanelOpen] = useState(false);
  /** Whether the view is pinned to the bottom. Follows new output only when it
   * is, so scrolling up to read is not yanked back by a streamed token. */
  const pinned = useRef(true);

  useEffect(() => {
    const node = scroller.current;
    if (node === null) return;
    const onScroll = (): void => {
      const distance = node.scrollHeight - node.scrollTop - node.clientHeight;
      pinned.current = distance < 80;
    };
    node.addEventListener("scroll", onScroll, { passive: true });
    return () => node.removeEventListener("scroll", onScroll);
  }, []);

  // `transcriptRevision` bumps on every streamed token; that is the signal to
  // follow the bottom, and it is deliberately the only dependency.
  useEffect(() => {
    const node = scroller.current;
    if (node !== null && pinned.current) node.scrollTop = node.scrollHeight;
  }, [state.transcriptRevision]);

  // Opening a different chat always lands at the bottom.
  useEffect(() => {
    const node = scroller.current;
    if (node !== null) {
      pinned.current = true;
      node.scrollTop = node.scrollHeight;
    }
  }, [state.openChatId]);

  if (chat === undefined) {
    return <EmptyPane onNewChat={() => store.openNewChat()} />;
  }

  const space = state.spaces.find((candidate) => candidate.id === chat.spaceId);
  const device = state.devices.find((candidate) => candidate.id === chat.deviceId);

  return (
    <div className="relative flex min-h-0 min-w-0 flex-1 overflow-hidden bg-background" data-chat-view="true">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
      <header className="relative flex h-[var(--workspace-topbar-height)] min-h-[var(--workspace-topbar-height)] shrink-0 items-center gap-2 bg-background px-3 sm:px-5" data-chat-header>
        <button
          type="button"
          onClick={onBack}
          aria-label="Back to conversations"
          className="-ml-1 flex size-8 shrink-0 items-center justify-center rounded-lg text-muted-foreground hover:bg-accent md:hidden"
        >
          <IconBack className="size-5" />
        </button>
        <nav aria-label="Thread breadcrumb" className="flex min-w-0 flex-1 items-center gap-2 text-sm sm:gap-3">
          {chat.spaceId !== null && chat.spaceId !== undefined ? (
            <>
              <span className="hidden min-w-0 shrink items-center gap-1.5 text-muted-foreground sm:flex">
                <span className="size-3.5 shrink-0 rounded-sm bg-accent" aria-hidden="true" />
                <span className="max-w-40 truncate">{space ? spaceLabel(space) : "Project"}</span>
              </span>
              <span className="hidden text-icon-muted sm:inline" aria-hidden="true">/</span>
            </>
          ) : null}
          <h1 className="min-w-0 truncate font-medium text-foreground">{chatTitle(chat)}</h1>
        </nav>
        {(chat.branch ?? "").length > 0 ? (
          <span className="hidden items-center gap-1 rounded-full border border-border px-2 py-1 text-2xs text-muted-foreground sm:flex">
            <IconBranch className="size-3" />
            <span className="max-w-[12rem] truncate">{chat.branch}</span>
          </span>
        ) : null}
        {device !== undefined ? (
          <span className="hidden items-center gap-1 rounded-full border border-border px-2 py-1 text-2xs text-muted-foreground sm:flex">
            <IconDevice className="size-3" />
            <span className="max-w-[10rem] truncate">{device.name ?? device.id}</span>
          </span>
        ) : null}
        <ChangeRequestButton onOpen={() => setPanelOpen((value) => !value)} />
      </header>

      <div ref={scroller} className="relative min-h-0 flex-1 overflow-y-auto overscroll-contain" data-chat-canvas>
        {store.transcriptView.entries.length === 0 && !state.historyPending ? (
          <p className="px-4 py-16 text-center text-sm text-muted-foreground">
            {store.statusFor(chat.id) === "working" ? "Working…" : "No messages yet."}
          </p>
        ) : (
          <Transcript
            entries={store.transcriptView.entries}
            historyPending={state.historyPending}
            onRespond={(inputId, answers) =>
              void store.respondInput(inputId, [
                { questionId: Object.keys(answers)[0] ?? "", labels: Object.values(answers) }
              ])
            }
          />
        )}
      </div>

      <QueueStrip />
      <Composer />
      </div>
      {panelOpen ? <ChangeRequestPanel onClose={() => setPanelOpen(false)} /> : null}
    </div>
  );
}

/** Chats that are waiting for an answer, offered as a jump list. The desktop
 * surfaces these prominently; on a phone the sidebar is hidden, so without this
 * an agent's question is easy to miss entirely. */
function QueueStrip() {
  const store = useStore();
  const waiting = store.awaitingInput();
  if (waiting.length === 0 || waiting.length === 1) return null;
  return (
    <div className="flex shrink-0 items-center gap-2 overflow-x-auto border-t border-border px-3 py-1.5">
      <span className="shrink-0 text-2xs text-muted-foreground">
        {waiting.length} waiting
      </span>
      {waiting.slice(0, 6).map((chat) => (
        <button
          key={chat.id}
          type="button"
          onClick={() => void store.openChat(chat.id)}
          className="shrink-0 truncate rounded-full border border-border px-2 py-0.5 text-2xs text-muted-foreground hover:bg-accent"
        >
          {chatTitle(chat)}
        </button>
      ))}
    </div>
  );
}

function EmptyPane({ onNewChat }: { onNewChat: () => void }) {
  const state = useAppState();
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center">
      <h2 className="text-lg font-medium">No conversation open</h2>
      <p className="max-w-sm text-sm text-muted-foreground">
        {state.devices.length === 0
          ? "Waiting for a device to come online."
          : "Pick a conversation from the sidebar, or start a new one."}
      </p>
      <button
        type="button"
        onClick={onNewChat}
        className="rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground"
      >
        New chat
      </button>
    </div>
  );
}
